import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ShieldCheck, Search, BookOpen } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full lg:grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden overflow-hidden bg-slate-950 lg:flex lg:flex-col lg:justify-between p-12">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #0284C7 0, transparent 40%), radial-gradient(circle at 80% 70%, #059669 0, transparent 40%)" }} />
        <div className="relative z-10"><Logo /></div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-heading text-4xl font-extrabold leading-tight tracking-tight text-white xl:text-5xl">
            Every reference book,<br /><span className="text-sky-400">one precise answer.</span>
          </h1>
          <p className="max-w-md text-sm leading-relaxed text-slate-300">
            The clinical knowledge platform for C&amp;A Pharmacy dispensers — instant, cited answers on dosage,
            interactions and contraindications, drawn from BNF, Uganda Clinical Guidelines and your approved sources.
          </p>
          <div className="grid grid-cols-3 gap-3 pt-4">
            {[["Cited", Search], ["Pooled cache", BookOpen], ["Governed", ShieldCheck]].map(([t, Icon]) => (
              <div key={t} className="rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                <Icon className="mb-2 h-5 w-5 text-emerald-400" />
                <div className="text-xs font-semibold text-white">{t}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">C&amp;A Pharmaceuticals Ltd · Uganda</div>
      </div>

      {/* Right form */}
      <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden"><Logo /></div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Use your branch, HOD or admin account.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@candapharmacy.com" data-testid="login-email-input" autoComplete="username" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="login-password-input" autoComplete="current-password" />
            </div>
            {error && (
              <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
